import { AmlImage, AmlImageError, BulkCmdError, Device as LegacyDevice, DeviceInfo as LegacyDeviceInfo, ReacquireNeededError, WipeMode, flashImage, parsePlatformConfig, type BurnProgress } from "@lineageos-infra/libamlburn";
import { LEGACY_AMLOGIC_PRODUCT_IDS } from "../../config/amlogic-legacy";
import { DeviceToolError } from "../errors";
import type { DeviceInfo, UsbProtocolService } from "../types";
import { parseSparseHeader } from "../fastboot/SparseImage";
import { isAmlogicAutoConnectDevice } from "../usb/UsbDeviceMatcher";
import { inspectAmlogicImage, type AmlogicImageReport } from "./AmlogicImageParser";
import { AmlogicProtocol, type AmlogicBurnOptions, type AmlogicHandshakeInfo } from "./AmlogicProtocol";
import { AmlogicUsbTransport } from "./AmlogicUsbTransport";
import type { V2ReacquireCoordinator } from "./multi/V2ReacquireCoordinator";

export type V2ReacquireDiagnosticPhase = "waiting" | "bound" | "ambiguous" | "timeout";

export interface V2ReacquireDiagnosticEvent {
  phase: V2ReacquireDiagnosticPhase;
  candidateCount: number;
  expectedStages?: number[];
  receivedStage?: number;
  connectionEpoch?: number;
}

export interface V2ReacquiredDeviceEvent {
  usbDevice: USBDevice;
  stageMinor: number;
}

export type V2ReacquiredDeviceHandler = (
  event: V2ReacquiredDeviceEvent,
) => number | void | Promise<number | void>;

export interface V3ReacquiredDeviceEvent {
  usbDevice: USBDevice;
}

export type V3ReacquiredDeviceHandler = (
  event: V3ReacquiredDeviceEvent,
) => number | void | Promise<number | void>;

export interface AmlogicLegacyBurnOptions {
  allowBootloader: boolean;
  skipSecureCheck: boolean;
  skipPackageCrc: boolean;
  directPartitionFlash: boolean;
  skipVerify: boolean;
  wipe: WipeMode;
  reboot: boolean;
  onProgress?: (progress: import("../types").OperationProgress) => void;
  /** Reports the exclusive reset/re-enumeration lease without exposing payload data. */
  onLeaseDiagnostic?: (event: { phase: "waiting" | "acquired" }) => void;
  /** Reports reacquire polling/binding outcomes; never carries firmware payload. */
  onReacquireDiagnostic?: (event: V2ReacquireDiagnosticEvent) => void;
}

function isLegacyProductId(productId?: number): boolean {
  return productId !== undefined && (LEGACY_AMLOGIC_PRODUCT_IDS as readonly number[]).includes(productId);
}

function isUsbAccessDenied(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current; depth += 1) {
    const message = current instanceof Error ? current.message : String(current);
    if ((typeof DOMException !== "undefined" && current instanceof DOMException && ["SecurityError", "NotAllowedError"].includes(current.name)) || /access denied|permission denied|insufficient permission/i.test(message)) return true;
    current = current instanceof Error ? current.cause : undefined;
  }
  return false;
}

function normalizeUbootCommand(command: string): string {
  const normalized = command.trim();
  if (!normalized) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "U-Boot command không được để trống.");
  if (normalized.length >= 127) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "U-Boot command phải ngắn hơn 127 ký tự.");
  if ([...normalized].some((character) => character.charCodeAt(0) < 0x20 || character.charCodeAt(0) > 0x7e)) {
    throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "U-Boot command chỉ được dùng ký tự ASCII in được.");
  }
  return normalized;
}

function normalizeLegacyPartitionName(name: string): string {
  const normalized = name.trim();
  if (!normalized || !/^[A-Za-z0-9._+-]+$/.test(normalized)) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Tên partition chỉ được dùng chữ, số, dấu chấm, gạch ngang hoặc gạch dưới.");
  return normalized;
}

export type LegacyPartitionImageFormat = "normal" | "sparse";

/** Reads only the Android sparse header; large partition images stay streaming. */
export async function detectLegacyPartitionImageFormat(file: Blob): Promise<LegacyPartitionImageFormat> {
  return await parseSparseHeader(file) ? "sparse" : "normal";
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function installSkipSecureCheck(device: LegacyDevice, enabled: boolean, onProgress: (progress: BurnProgress) => void): void {
  if (!enabled) return;
  const originalCheckBulkCmd = device.checkBulkCmd.bind(device);
  const originalReadMedia = device.readMedia.bind(device);
  let secureProbeSkipped = false;
  device.checkBulkCmd = async (command, commandOptions) => {
    if (/^upload mem 0x[0-9a-f]+ normal 0x4$/i.test(command.trim())) {
      secureProbeSkipped = true;
      onProgress({ stage: "secure-check" });
      return "success";
    }
    return originalCheckBulkCmd(command, commandOptions);
  };
  device.readMedia = async (size, timeout) => {
    if (secureProbeSkipped && size === 4) return new Uint8Array(4);
    return originalReadMedia(size, timeout);
  };
}

function readUint32LE(bytes: Uint8Array): number {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, true);
}

function hasPartitionItems(image: AmlImage): boolean {
  return image.items().some((item) => item.mainType.toUpperCase() === "PARTITION");
}

function hasUnencryptedUboot(image: AmlImage): boolean {
  return Boolean(image.itemGet("USB", "UBOOT"));
}

async function flashPartitionsDirect(
  device: LegacyDevice,
  image: AmlImage,
  options: { wipe: WipeMode; reboot: boolean; skipSecureCheck: boolean; skipVerify: boolean; onProgress: (progress: BurnProgress) => void },
): Promise<LegacyDevice> {
  const info = await device.identify();
  if (info.stageMinor !== LegacyDeviceInfo.STAGE_MINOR_TPL) {
    throw new AmlImageError(`direct partition flash requires TPL; current stage is ${info.toString()}`);
  }
  const platformItem = image.itemGet("conf", "platform");
  if (!platformItem) throw new AmlImageError("the image does not contain a platform config");
  const platform = parsePlatformConfig(await platformItem.text());
  const partitions = image.items().filter((item) => item.mainType.toUpperCase() === "PARTITION");
  if (partitions.length === 0) throw new AmlImageError("the image does not contain any PARTITION items");

  options.onProgress({ stage: "secure-check" });
  if (!options.skipSecureCheck) {
    if (!platform.encryptReg || platform.encryptReg === 0xffffffff) throw new AmlImageError("the package has no usable encrypt register");
    await device.checkBulkCmd(`upload mem 0x${platform.encryptReg.toString(16)} normal 0x4`);
    let secureBytes: Uint8Array;
    try {
      secureBytes = await device.readMedia(4);
    } catch {
      secureBytes = await device.readSimpleMemory(platform.encryptReg, 4);
    }
    const secureValue = readUint32LE(secureBytes);
    options.onProgress({ stage: "secure-check", bytesTransferred: secureValue & 16 ? 1 : 0, totalBytes: 1 });
  }

  options.onProgress({ stage: "disk-initial" });
  await device.checkBulkCmd(`disk_initial ${options.wipe}`);
  for (const item of partitions) {
    const partitionName = item.subType.trim();
    if (!/^[A-Za-z0-9._+-]+$/.test(partitionName)) throw new AmlImageError(`invalid partition name '${partitionName}'`);
    options.onProgress({ stage: "partition", partition: partitionName, bytesTransferred: 0, totalBytes: item.size });
    await device.checkTplCommand(`download store ${partitionName} ${item.fileType} ${item.size}`);
    await device.writeMediaStream(item.blob, {
      onProgress: (progress) => options.onProgress({ stage: "partition", partition: partitionName, ...progress }),
    });
    await device.checkBulkCmd("download get_status");
    if (!options.skipVerify && item.verify) {
      const verifyItem = image.itemGet("VERIFY", partitionName);
      if (verifyItem) {
        options.onProgress({ stage: "verify", partition: partitionName });
        await device.checkBulkCmd(`verify ${(await verifyItem.text()).trim()}`);
      }
    }
  }
  options.onProgress({ stage: "finish" });
  await device.checkBulkCmd("save_setting");
  try {
    await device.checkBulkCmd(`burn_complete ${options.reboot ? 1 : 3}`);
  } catch (error) {
    if (!(error instanceof BulkCmdError)) throw error;
  }
  return device;
}

export class AmlogicService implements UsbProtocolService {
  readonly transport = new AmlogicUsbTransport();
  readonly protocol = new AmlogicProtocol(this.transport);
  private legacySelected = false;
  private legacyFlashDevice?: LegacyDevice;
  private legacyCancelRequested = false;
  private legacyReconnecting = false;
  private adnlReconnecting = false;

  /** `reacquireCoordinator` is undefined for standalone use (e.g. tests); the manager always injects one. */
  constructor(
    private readonly reacquireCoordinator?: V2ReacquireCoordinator,
    private readonly onLegacyReacquired?: V2ReacquiredDeviceHandler,
    private readonly onAdnlReacquired?: V3ReacquiredDeviceHandler,
    private readonly isUsbDeviceClaimedByOtherSession: (device: USBDevice) => boolean = () => false,
  ) {}

  async connect(): Promise<void> {
    await this.connectInternal();
  }

  /**
   * Connect to an already-authorized Amlogic device without opening the
   * browser's permission picker. This allows V2/V3 auto-connect on USB attach.
   */
  async connectAuthorizedDevice(device: USBDevice): Promise<void> {
    await this.connectInternal(device);
  }

  private async connectInternal(authorizedDevice?: USBDevice): Promise<void> {
    this.legacySelected = false;
    this.protocol.markDisconnected();
    const options = { selectionOnly: (device: USBDevice) => isLegacyProductId(device.productId) };
    if (authorizedDevice) await this.transport.connectAuthorizedDevice(authorizedDevice, options);
    else await this.transport.connect(options);

    if (isLegacyProductId(this.transport.usbDevice?.productId)) {
      this.legacySelected = true;
      return;
    }

    try {
      await this.protocol.handshake();
    } catch (error) {
      await this.disconnect().catch(() => undefined);
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    this.legacyCancelRequested = true;
    await this.legacyFlashDevice?.close().catch(() => undefined);
    this.legacyFlashDevice = undefined;
    this.legacySelected = false;
    await this.transport.disconnect();
    this.protocol.markDisconnected();
  }

  isConnected(): boolean {
    return this.legacySelected ? Boolean(this.transport.usbDevice) : this.transport.isConnected();
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const device = this.transport.usbDevice;
    if (this.legacySelected) {
      if (!device) throw new DeviceToolError("DEVICE_NOT_SELECTED");
      const match = this.transport.interfaceMatch;
      return {
        protocol: "AMLOGIC",
        vendorId: device.vendorId,
        productId: device.productId,
        manufacturer: device.manufacturerName,
        product: device.productName,
        serial: device.serialNumber,
        usbVersion: `${device.usbVersionMajor}.${device.usbVersionMinor}.${device.usbVersionSubminor}`,
        interfaceNumber: match?.interfaceNumber,
        endpointIn: match?.endpointIn?.endpointNumber,
        endpointOut: match?.endpointOut?.endpointNumber,
        interfaceClaimed: false,
        protocolVersion: "USB Burning Tool v2 / WorldCup · chỉ nhận diện",
        chipFamily: "S905X / S905X2 / S905X3 · Legacy",
      };
    }

    const info = await this.transport.getDeviceInfo();
    const handshake = this.protocol.handshakeInfo;
    return {
      ...info,
      protocolVersion: handshake ? `ADNL ${handshake.stage} · identify ${handshake.identify}` : "ADNL chưa handshake",
      chipFamily: handshake ? `S4 / S905W2 · ${handshake.stage}` : "Chưa xác định",
    };
  }

  get handshakeInfo(): AmlogicHandshakeInfo | undefined {
    return this.protocol.handshakeInfo;
  }

  get isLegacyDeviceSelected(): boolean {
    return this.legacySelected;
  }

  /** True while the BL1/BL2 chain is changing USB gadgets or being reopened. */
  get isReconnecting(): boolean {
    return this.legacyReconnecting || this.adnlReconnecting || this.protocol.isBootstrapping;
  }

  async inspectImage(file: File): Promise<AmlogicImageReport> {
    return inspectAmlogicImage(file);
  }

  async burn(file: File, options: AmlogicBurnOptions): Promise<void> {
    if (this.legacySelected) {
      throw new DeviceToolError("UNSUPPORTED_IMAGE", "Thiết bị PID 0xC003 dùng USB Burning Tool v2; không gửi package này bằng ADNL v3.");
    }
    if (!this.isConnected()) throw new DeviceToolError("DEVICE_DISCONNECTED");
    await this.protocol.burn(file, {
      ...options,
      reconnect: options.reconnect ?? (() => this.reconnectAfterBl1()),
    });
  }

  private async reconnectAfterBl1(): Promise<void> {
    const usb = navigator.usb;
    if (!usb) throw new DeviceToolError("WEBUSB_UNSUPPORTED");
    this.adnlReconnecting = true;
    try {
      // The ROM/BL2 chain tears down the old gadget before U-Boot creates a
      // new one. Do not let the stale endpoint receive post-bootstrap data.
      const previousDevice = this.transport.usbDevice;
      const previousSerial = previousDevice?.serialNumber;
      await this.transport.disconnect();
      const deadline = Date.now() + 20_000;
      let lastError: unknown;
      while (Date.now() < deadline) {
        const devices = await usb.getDevices().catch(() => [] as USBDevice[]);
        const ownedOrUnclaimed = devices.filter((device) => (
          device.vendorId === 0x1b8e
          && device.productId !== 0xc003
          && isAmlogicAutoConnectDevice(device)
          && !this.isUsbDeviceClaimedByOtherSession(device)
        ));
        const serialMatches = previousSerial ? ownedOrUnclaimed.filter((device) => device.serialNumber === previousSerial) : [];
        const identityMatches = serialMatches.length > 0 ? serialMatches : ownedOrUnclaimed;
        // Chromium may either create a new USBDevice object or reuse the old
        // one after re-enumeration. Prefer a fresh handle, but keep the old
        // object as a valid fallback when it is the only authorized candidate.
        const freshHandles = identityMatches.filter((device) => device !== previousDevice);
        const candidates = freshHandles.length > 0 ? freshHandles : identityMatches;
        if (candidates.length > 1) {
          lastError = new DeviceToolError("DEVICE_BUSY", `Có ${candidates.length} interface ADNL mới chưa được gán trong lúc V3 re-enumerate; đã dừng để tránh nối nhầm thiết bị.`);
        }
        const candidate = candidates.length === 1 ? candidates[0] : undefined;
        if (candidate) {
          try {
            await this.transport.connectAuthorizedDevice(candidate);
            await this.onAdnlReacquired?.({ usbDevice: candidate });
            return;
          } catch (error) {
            lastError = error;
            await this.transport.disconnect().catch(() => undefined);
          }
        }
        await wait(250);
      }
      throw new DeviceToolError("DEVICE_BUSY", "BL2 đã nạp xong nhưng không tìm thấy lại interface ADNL U-Boot sau 20 giây. Hãy kiểm tra cáp/driver và kết nối lại thiết bị.", undefined, lastError);
    } finally {
      this.adnlReconnecting = false;
    }
  }

  async burnAdnlPartition(file: File, partitionName: string, options: { allowBootloader: boolean; skipVerify?: boolean; diskInitialZero?: boolean; onProgress?: (progress: import("../types").OperationProgress) => void }): Promise<void> {
    if (this.legacySelected) {
      throw new DeviceToolError("UNSUPPORTED_IMAGE", "Thiết bị hiện tại dùng USB Burning Tool v2; hãy dùng panel Flash partition V2.");
    }
    if (!this.isConnected()) throw new DeviceToolError("DEVICE_DISCONNECTED");
    const normalized = normalizeLegacyPartitionName(partitionName);
    await this.protocol.burnPartition(file, normalized, {
      experimental: true,
      allowBootloader: options.allowBootloader,
      skipVerify: options.skipVerify,
      diskInitialZero: options.diskInitialZero,
      onProgress: options.onProgress,
    });
  }

  async burnLegacy(file: File, options: AmlogicLegacyBurnOptions): Promise<void> {
    if (!this.legacySelected || !this.transport.usbDevice) {
      throw new DeviceToolError("DEVICE_DISCONNECTED", "Chưa chọn thiết bị Legacy v2.");
    }
    const usb = navigator.usb;
    if (!usb) throw new DeviceToolError("WEBUSB_UNSUPPORTED");

    this.legacyCancelRequested = false;
    const startedAt = Date.now();
    const reportProgress = (progress: BurnProgress) => {
      if (this.legacyCancelRequested) throw new DeviceToolError("OPERATION_CANCELLED");
      const partition = progress.partition ? ` · ${progress.partition}` : "";
      options.onProgress?.({
        phase: `Legacy v2 · ${progress.stage}${partition}`,
        completedBytes: progress.bytesTransferred ?? 0,
        totalBytes: progress.totalBytes,
        startedAt,
      });
    };
    const reportReconnect = (phase: string) => {
      options.onProgress?.({ phase, completedBytes: 0, totalBytes: 1, startedAt });
    };

    this.legacyReconnecting = true;
    let releaseLease: (() => void) | undefined;
    try {
      const image = await AmlImage.open(file, { verifyCrc: !options.skipPackageCrc });
      if (options.skipVerify) {
        for (const item of image.items()) Object.defineProperty(item, "verify", { value: false, writable: false });
      }
      const device = new LegacyDevice(this.transport.usbDevice, { logging: false });
      this.legacyFlashDevice = device;
      await device.initialize();
      installSkipSecureCheck(device, options.skipSecureCheck, reportProgress);
      const directPartitionFlash = options.directPartitionFlash || (!hasUnencryptedUboot(image) && hasPartitionItems(image));
      // Full-package flow resets BL1/SPL/TPL and re-enumerates; serialize it
      // globally so two V2 boxes never disappear/reappear at the same time.
      if (!directPartitionFlash) {
        options.onLeaseDiagnostic?.({ phase: "waiting" });
        releaseLease = await this.reacquireCoordinator?.acquireLease();
        options.onLeaseDiagnostic?.({ phase: "acquired" });
      }
      const result = directPartitionFlash
        ? await flashPartitionsDirect(device, image, {
          wipe: options.wipe,
          reboot: options.reboot,
          skipSecureCheck: options.skipSecureCheck,
          skipVerify: options.skipVerify,
          onProgress: reportProgress,
        })
        : await flashImage(device, image, {
          wipe: options.wipe,
          reboot: options.reboot,
          noEraseBootloader: options.skipSecureCheck || !options.allowBootloader,
          onProgress: reportProgress,
          reacquire: () => this.reacquireLegacyV2Device(usb, options.skipSecureCheck, reportProgress, reportReconnect, options.onReacquireDiagnostic),
        });
      this.legacyFlashDevice = result;
      reportProgress({ stage: "finish" });
    } catch (error) {
      if (error instanceof DeviceToolError) throw error;
      if (error instanceof AmlImageError) throw new DeviceToolError("INVALID_IMAGE", `Legacy v2: ${error.message}`, undefined, error);
      if (error instanceof ReacquireNeededError) {
        throw new DeviceToolError("DEVICE_BUSY", "Thiết bị đã reset trong quá trình flash nhưng trình duyệt không giữ quyền WebUSB. Hãy kết nối lại rồi thử lại.", undefined, error);
      }
      if (isUsbAccessDenied(error)) {
        throw new DeviceToolError("USB_ACCESS_DENIED", "WebUSB bị từ chối. Hãy gán đúng driver WinUSB cho interface Amlogic rồi kết nối lại.", undefined, error);
      }
      const detail = error instanceof Error ? error.message : String(error);
      if (/platform .* not supported|unexpected stage|invalid power state/i.test(detail)) {
        throw new DeviceToolError("UNSUPPORTED_IMAGE", `Legacy v2 không dùng được package/trạng thái này: ${detail}`, undefined, error);
      }
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Legacy v2: ${detail}`, undefined, error);
    } finally {
      await this.legacyFlashDevice?.close().catch(() => undefined);
      this.legacyFlashDevice = undefined;
      this.legacyReconnecting = false;
      releaseLease?.();
    }
  }

  private async reacquireLegacyV2Device(
    usb: USB,
    skipSecureCheck: boolean,
    onProgress: (progress: BurnProgress) => void,
    reportReconnect: (phase: string) => void,
    onDiagnostic?: (event: V2ReacquireDiagnosticEvent) => void,
  ): Promise<LegacyDevice> {
    reportReconnect("Legacy v2 · reconnect · chờ thiết bị sau U-Boot");

    // The WorldCup gadget needs time to detach its old control endpoint and
    // enumerate the new TPL/U-Boot endpoint. Opening immediately can succeed
    // but the first disk_initial control transfer then stalls.
    await wait(1_500);

    const expectedStages = [
      LegacyDeviceInfo.STAGE_MINOR_IPL,
      LegacyDeviceInfo.STAGE_MINOR_SPL,
      LegacyDeviceInfo.STAGE_MINOR_TPL,
    ];
    type LegacyProbe = { device: LegacyDevice; stageMinor: number };
    const probe = async (candidate: USBDevice): Promise<LegacyProbe | undefined> => {
      const reacquired = new LegacyDevice(candidate, { logging: false });
      try {
        await reacquired.initialize();
        const info = await reacquired.identify();
        const validStage = expectedStages.includes(info.stageMinor);
        if (!validStage) throw new Error(`unexpected stage after reconnect: ${info.toString()}`);
        return { device: reacquired, stageMinor: info.stageMinor };
      } catch {
        await reacquired.close().catch(() => undefined);
        return undefined;
      }
    };

    if (this.reacquireCoordinator) {
      onDiagnostic?.({ phase: "waiting", candidateCount: 0 });
      const outcome = await this.reacquireCoordinator.resolveCandidate({
        usb,
        deadlineMs: 20_000,
        probe,
        dispose: (result) => result.device.close(),
        onPoll: (candidateCount) => onDiagnostic?.({ phase: "waiting", candidateCount }),
      });
      if (outcome.status === "bound" && outcome.device && outcome.usbDevice) {
        const result = outcome.device;
        let connectionEpoch: number | void;
        try {
          await this.transport.connectAuthorizedDevice(outcome.usbDevice, {
            selectionOnly: (device) => isLegacyProductId(device.productId),
          });
          this.legacySelected = true;
          connectionEpoch = await this.onLegacyReacquired?.({
            usbDevice: outcome.usbDevice,
            stageMinor: result.stageMinor,
          });
        } catch (error) {
          await result.device.close().catch(() => undefined);
          throw error;
        }
        installSkipSecureCheck(result.device, skipSecureCheck, onProgress);
        this.legacyFlashDevice = result.device;
        onDiagnostic?.({
          phase: "bound",
          candidateCount: 1,
          expectedStages,
          receivedStage: result.stageMinor,
          connectionEpoch: typeof connectionEpoch === "number" ? connectionEpoch : undefined,
        });
        reportReconnect("Legacy v2 · reconnect · đã kết nối lại");
        return result.device;
      }
      if (outcome.status === "ambiguous") {
        onDiagnostic?.({ phase: "ambiguous", candidateCount: outcome.candidateCount, expectedStages });
        throw new DeviceToolError(
          "DEVICE_BUSY",
          `Phát hiện ${outcome.candidateCount} thiết bị V2 giống hệt nhau xuất hiện cùng lúc sau reset; không thể xác định an toàn thiết bị nào vừa reset. Hãy rút các thiết bị V2 không liên quan rồi kết nối lại từng thiết bị một.`,
        );
      }
      onDiagnostic?.({ phase: "timeout", candidateCount: 0, expectedStages });
      throw new DeviceToolError(
        "DEVICE_BUSY",
        "Legacy v2 đã reset nhưng không mở lại được WorldCup/TPL trong 20 giây. Hãy kiểm tra driver và cáp USB.",
      );
    }

    // No coordinator injected (standalone AmlogicService, e.g. direct unit use):
    // fall back to the original single-device polling loop.
    const deadline = Date.now() + 20_000;
    let lastError: unknown;
    while (Date.now() < deadline) {
      const devices = await usb.getDevices().catch(() => [] as USBDevice[]);
      const candidates = devices.filter((item) => item.vendorId === 0x1b8e && item.productId === 0xc003);
      for (const candidate of candidates) {
        const reacquired = await probe(candidate);
        if (reacquired) {
          installSkipSecureCheck(reacquired.device, skipSecureCheck, onProgress);
          this.legacyFlashDevice = reacquired.device;
          reportReconnect("Legacy v2 · reconnect · đã kết nối lại");
          return reacquired.device;
        }
      }
      await wait(250);
    }

    throw new DeviceToolError(
      "DEVICE_BUSY",
      "Legacy v2 đã reset nhưng không mở lại được WorldCup/TPL trong 20 giây. Hãy kiểm tra driver và cáp USB.",
      undefined,
      lastError,
    );
  }

  async runLegacyCommand(command: string): Promise<string> {
    if (!this.legacySelected || !this.transport.usbDevice) {
      throw new DeviceToolError("DEVICE_DISCONNECTED", "Chưa chọn thiết bị Legacy v2.");
    }
    const normalized = normalizeUbootCommand(command);
    this.legacyCancelRequested = false;
    try {
      const device = new LegacyDevice(this.transport.usbDevice, { logging: false });
      this.legacyFlashDevice = device;
      await device.initialize();
      const info = await device.identify();
      if (info.stageMinor !== LegacyDeviceInfo.STAGE_MINOR_TPL) throw new DeviceToolError("UNSUPPORTED_IMAGE", `U-Boot command cần thiết bị ở TPL; stage hiện tại là ${info.toString()}.`);
      return await device.checkBulkCmd(normalized, { timeout: 10_000, busyRetryDelay: 500 });
    } catch (error) {
      if (isUsbAccessDenied(error)) {
        throw new DeviceToolError("USB_ACCESS_DENIED", "WebUSB bị từ chối. Hãy gán đúng driver WinUSB cho interface Amlogic rồi kết nối lại.", undefined, error);
      }
      if (error instanceof DeviceToolError) throw error;
      const detail = error instanceof Error ? error.message : String(error);
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Legacy v2 command: ${detail}`, undefined, error);
    } finally {
      await this.legacyFlashDevice?.close().catch(() => undefined);
      this.legacyFlashDevice = undefined;
    }
  }

  async runAdnlCommand(command: string): Promise<string> {
    if (this.legacySelected) {
      throw new DeviceToolError("UNSUPPORTED_IMAGE", "Thiết bị hiện tại dùng USB Burning Tool v2; hãy dùng panel U-Boot command V2.");
    }
    if (!this.isConnected()) throw new DeviceToolError("DEVICE_DISCONNECTED");
    const normalized = normalizeUbootCommand(command);
    return this.protocol.runUbootCommand(normalized);
  }

  async burnLegacyPartition(file: File, partitionName: string, onProgress?: (progress: import("../types").OperationProgress) => void): Promise<void> {
    if (!this.legacySelected || !this.transport.usbDevice) throw new DeviceToolError("DEVICE_DISCONNECTED", "Chưa chọn thiết bị Legacy v2.");
    if (!file.size) throw new DeviceToolError("INVALID_IMAGE", "File partition rỗng.");
    const normalized = normalizeLegacyPartitionName(partitionName);
    const format = await detectLegacyPartitionImageFormat(file);
    const startedAt = Date.now();
    const reportProgress = (completedBytes: number, totalBytes?: number, phase = `Legacy v2 · partition · ${normalized}`) => {
      if (this.legacyCancelRequested) throw new DeviceToolError("OPERATION_CANCELLED");
      onProgress?.({ phase, completedBytes, totalBytes, startedAt });
    };
    this.legacyCancelRequested = false;
    try {
      const device = new LegacyDevice(this.transport.usbDevice, { logging: false });
      this.legacyFlashDevice = device;
      await device.initialize();
      const info = await device.identify();
      if (info.stageMinor !== LegacyDeviceInfo.STAGE_MINOR_TPL) throw new DeviceToolError("UNSUPPORTED_IMAGE", `Flash partition cần thiết bị ở TPL; stage hiện tại là ${info.toString()}.`);
      reportProgress(0, file.size, `Legacy v2 · download store ${normalized} ${format}`);
      await device.checkTplCommand(`download store ${normalized} ${format} ${file.size}`);
      await device.writeMediaStream(file, { onProgress: (progress) => reportProgress(progress.bytesTransferred, progress.totalBytes) });
      await device.checkBulkCmd("download get_status");
      reportProgress(file.size, file.size, `Legacy v2 · partition ${normalized} (${format}) hoàn tất`);
    } catch (error) {
      if (isUsbAccessDenied(error)) {
        throw new DeviceToolError("USB_ACCESS_DENIED", "WebUSB bị từ chối. Hãy gán đúng driver WinUSB cho interface Amlogic rồi kết nối lại.", undefined, error);
      }
      if (error instanceof DeviceToolError) throw error;
      const detail = error instanceof Error ? error.message : String(error);
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Legacy v2 partition: ${detail}`, undefined, error);
    } finally {
      await this.legacyFlashDevice?.close().catch(() => undefined);
      this.legacyFlashDevice = undefined;
    }
  }

  async cancelCurrentOperation(): Promise<void> {
    this.legacyCancelRequested = true;
    await this.protocol.cancelCurrentOperation();
  }
}
